import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { TextareaFieldStyle } from './types'

// Simple ID generator
let idCounter = 0
function generateId(): string {
  return `mdc-textarea-field-${++idCounter}`
}

@safeCustomElement('mdc-textarea-field')
export class MdcTextareaField extends LitElement {
  static formAssociated = true

  @property({ type: String }) variant: TextareaFieldStyle = 'secondary'
  @property({ type: String }) label = ''
  @property({ type: String }) hint = ''
  @property({ type: String }) placeholder = ''
  @property({ type: String }) value = ''
  @property({ type: String }) name = ''
  @property({ type: String }) id = ''
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) required = false
  @property({ type: Number }) rows = 4
  @property({ type: Number }) maxlength = 0
  @property({ type: String }) resize: 'none' | 'vertical' | 'both' = 'vertical'
  @property({ type: String, attribute: 'validation' }) validationStr = ''

  private textareaId = generateId()
  private _internals!: ElementInternals

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  get validation() {
    if (!this.validationStr) return null
    try {
      return JSON.parse(this.validationStr)
    } catch {
      return null
    }
  }

  set validation(value) {
    this.validationStr = value ? JSON.stringify(value) : ''
  }

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-textarea-field-wrapper {
			--_mdc-textarea-field__input-padding-sides: var(--form-field-padding-horizontal, 0.75rem);
			--_mdc-textarea-field__input-padding-vertical: var(--form-field-padding-vertical, 0.5rem);

			display: flex;
			flex-direction: column;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-textarea-field__label-row {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-textarea-field__label {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1;
		}

		.mdc-textarea-field__input {
			box-sizing: border-box;
			appearance: none;
			margin: 0;
			padding: 0;
			border: 0;
			background: none;
			display: block;
			width: 100%;
			min-height: 4rem;
			font-size: var(--form-field-font-size, 0.875rem);
			color: var(--color-functional-gray-black);
			font-weight: 450;
			line-height: 1.5;
			border: 1px solid transparent;
			border-radius: var(--radius-md, 0.5rem);
			padding: var(--_mdc-textarea-field__input-padding-vertical) var(--_mdc-textarea-field__input-padding-sides);
			background-color: var(--color-functional-gray-white);
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-textarea-field__input {
				font-size: var(--form-field-font-size-mobile, 1rem);
			}
		}

		.mdc-textarea-field__input::placeholder {
			font-size: var(--form-field-font-size, 0.875rem);
			color: var(--color-functional-gray-200);
			font-weight: 450;
			line-height: 1.5;
		}

		/* breakpoints.md - 1 */
		@media (max-width: 767px) {
			.mdc-textarea-field__input::placeholder {
				font-size: var(--form-field-font-size-mobile, 1rem);
			}
		}

		.mdc-textarea-field__input:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-textarea-field__hint {
			font-size: var(--text-075, 0.75rem);
			color: var(--color-functional-gray-400);
			font-weight: 450;
			line-height: 1.3;
		}

		.mdc-textarea-field-wrapper--validation-error .mdc-textarea-field__input {
			border-color: var(--color-functional-red-500);
		}

		.mdc-textarea-field-wrapper--validation-error .mdc-textarea-field__input:focus-visible {
			box-shadow: 0 0 0 2px rgba(var(--color-functional-red-500-rgb, 239, 68, 68), 0.5);
			outline: none;
		}

		.mdc-textarea-field-wrapper--validation-error .mdc-textarea-field__hint {
			color: var(--color-functional-red-500);
		}

		.mdc-textarea-field__input:disabled {
			color: var(--color-functional-gray-300);
			background-color: var(--color-functional-gray-50);
		}

		.mdc-textarea-field__input:disabled::placeholder {
			color: var(--color-functional-gray-200);
		}

		.mdc-textarea-field-wrapper--disabled .mdc-textarea-field__label {
			color: var(--color-functional-gray-400);
		}

		.mdc-textarea-field-wrapper--style-secondary .mdc-textarea-field__input {
			border-color: var(--color-functional-gray-100);
		}

		.mdc-textarea-field-wrapper--style-tertiary .mdc-textarea-field__input {
			transition: background-color 0.2s ease;
		}

		.mdc-textarea-field-wrapper--style-tertiary .mdc-textarea-field__input:hover:not(:disabled),
		.mdc-textarea-field-wrapper--style-tertiary .mdc-textarea-field__input:focus-visible:not(:disabled) {
			background-color: var(--color-functional-gray-25);
		}

		.mdc-textarea-field-wrapper--style-tertiary .mdc-textarea-field__input:disabled {
			background-color: var(--color-functional-gray-50);
		}
	`

  connectedCallback() {
    super.connectedCallback()
    if (this.id) {
      this.textareaId = this.id
    } else if (!this.textareaId) {
      this.textareaId = generateId()
    }
    this._internals.setFormValue(this.value || null)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('value') || changedProperties.has('name')) {
      this._internals.setFormValue(this.value || null)
      this._updateValidity()
    }
  }

  private _updateValidity() {
    const textarea = this.shadowRoot?.querySelector('textarea')
    if (textarea) {
      this._internals.setValidity(textarea.validity, textarea.validationMessage, textarea)
    }
  }

  formResetCallback() {
    this.value = ''
    this._internals.setFormValue(null)
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  formStateRestoreCallback(state: string) {
    this.value = state
    this._internals.setFormValue(this.value || null)
  }

  private handleFocus(e: FocusEvent) {
    this.dispatchEvent(
      new CustomEvent('focus', {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleBlur(e: FocusEvent) {
    this.dispatchEvent(
      new CustomEvent('blur', {
        detail: { originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleChange(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    this.value = textarea.value
    this._internals.setFormValue(this.value || null)
    this._updateValidity()
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: this.value, originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const validation = this.validation
    const classes = [
      'mdc-textarea-field-wrapper',
      `mdc-textarea-field-wrapper--style-${this.variant}`,
      validation ? `mdc-textarea-field-wrapper--validation-${validation.status}` : '',
      this.disabled ? 'mdc-textarea-field-wrapper--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return html`
			<div class=${classes}>
				<div class="mdc-textarea-field__label-row">
					<label for=${this.textareaId} class="mdc-textarea-field__label">${this.label}</label>
					<slot name="label-action"></slot>
				</div>
				<textarea
					id=${this.textareaId}
					class="mdc-textarea-field__input"
					placeholder=${this.placeholder}
					.value=${this.value}
					name=${this.name || ''}
					rows=${this.rows}
					maxlength=${this.maxlength > 0 ? this.maxlength : nothing}
					style="resize: ${this.resize}"
					?disabled=${this.disabled}
					?required=${this.required}
					@focus=${this.handleFocus}
					@blur=${this.handleBlur}
					@input=${this.handleChange}
				></textarea>
				${this.hint ? html`<span class="mdc-textarea-field__hint">${this.hint}</span>` : ''}
			</div>
		`
  }
}
